/**
 * @file ProductGroups.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description Manage Inventory → Product Groups (Cam, 28 Oct 2026 — O1):
 *              the same product from different suppliers, one group per pack
 *              (q-product-groups-2), its additive versions inside it. Lists
 *              the groups (search every word, a category filter, archived
 *              ones on request) with their items, suppliers, pack and
 *              additive versions; Add group; each row opens the group's
 *              window (ProductGroupModal — rename, notes, items in / out,
 *              variant labels, archive / restore, history). Reading needs
 *              Manage Inventory; changes need 'Edit inventory items' (the
 *              server says which, `can_edit`). Before
 *              migrations/product_groups.sql it says so and does nothing.
 *              Pricing & Costing reads the groups for each row's highest
 *              bill cost this period and the customer's usual supplier.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './ProductGroups.css';
import Input from './Input';
import MultiSelectDropdown from './MultiSelectDropdown';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import RowActionsMenu from './RowActionsMenu';
import TableContextMenu from './TableContextMenu';
import SearchHelp from './SearchHelp';
import ProductGroupModal from './ProductGroupModal';
import AuditHistoryModal from './AuditHistoryModal';
import { useSlideOutButton } from './SlideOutButton';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useLatestRequest from '../hooks/useLatestRequest';
import usePersistedState from '../hooks/usePersistedState';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { NOT_READY_REASON, OPEN_GROUP_KEY, packLabel } from '../utils/productGroups';
import { formatDate } from '../utils/dateFormatters';

const CLEARED_FILTERS = { search: '', categories: [], includeArchived: false };
const PAGE_SIZE = 50;

function ProductGroups() {
  const [filters, setFilters] = usePersistedState('productGroups.filters', CLEARED_FILTERS);
  const [page, setPage] = usePersistedState('productGroups.page', 1);
  const [openId, setOpenId] = usePersistedState(OPEN_GROUP_KEY, null);
  const [creating, setCreating] = useState(false);
  const [history, setHistory] = useState(null);
  const [data, setData] = useState({ ready: true, groups: [], categories: [], can_edit: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const latest = useLatestRequest();

  useSlideOutButton([loading, data.can_edit]);

  const load = useCallback(async (f) => {
    const ticket = latest.begin();
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (f.search) qs.set('search', f.search);
      if (f.categories.length) qs.set('categories', f.categories.join(','));
      if (f.includeArchived) qs.set('includeArchived', 'true');
      const res = await fetch(`${API_URL}/api/product-groups?${qs}`, { credentials: 'include' });
      const body = await res.json();
      if (!latest.isLatest(ticket)) return;
      if (!res.ok) throw new Error(body.error || 'Could not read the product groups');
      setData(body);
      setError(null);
    } catch (e) {
      if (latest.isLatest(ticket)) setError(e.message);
    } finally {
      if (latest.isLatest(ticket)) setLoading(false);
    }
  }, [latest]);

  // Typing searches after a short pause; other filters at once.
  useEffect(() => {
    const t = setTimeout(() => load(filters), filters.search ? 300 : 0);
    return () => clearTimeout(t);
  }, [filters, load]);

  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS);
  const filtersApplied = headerFilters.count > 0;
  const setFilter = (k, v) => { setFilters(prev => ({ ...prev, [k]: v })); setPage(1); };
  const clearFilters = () => { setFilters(CLEARED_FILTERS); setPage(1); };

  const groups = data.groups || [];
  const totalPages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  const current = Math.min(page || 1, totalPages);
  const shown = groups.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const ready = data.ready !== false;
  const canEdit = ready && !!data.can_edit;

  const ctxMenu = useTableContextMenu({ rows: shown, getRowId: (g) => g.id });
  const rowActions = (g) => [
    { key: 'open', icon: 'open_in_new', label: 'Open', onClick: () => setOpenId(g.id) },
    { key: 'history', icon: 'history', label: 'History', onClick: () => setHistory(g) }
  ];

  const closeWindow = () => { setOpenId(null); setCreating(false); load(filters); };

  return (
    <div className="product-groups contract-pricing">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            The same product from different suppliers, one group per pack (bulk, totes, 25kg bags …), with its additive versions inside it.
            Pricing & Costing compares the group's bill costs this season and the customer's usual supplier.
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className={clearFiltersClass(filtersApplied)} onClick={clearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" data-write-action onClick={() => setCreating(true)} disabled={!canEdit}
                title={!ready ? NOT_READY_REASON : canEdit ? 'Make a new product group' : "Making groups needs 'Edit inventory items'"}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Add Group</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp fields={['group name', 'category', 'notes', 'item code', 'item name', 'variant / additive']} />
            </label>
            <Input type="text" icon="search" placeholder="Group, item code or name…"
              value={filters.search} onChange={(e) => setFilter('search', e.target.value)} />
          </div>
          <div className="filter-group">
            <label>Category</label>
            <MultiSelectDropdown
              options={(data.categories || []).map(c => ({ value: c, label: c }))}
              value={filters.categories}
              onChange={(v) => setFilter('categories', v)}
              placeholder="All categories"
              searchable
            />
          </div>
          <div className="filter-group pg-toggle">
            <label htmlFor="pg-archived">Show archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="pg-archived" type="checkbox" checked={filters.includeArchived}
                  onChange={(e) => setFilter('includeArchived', e.target.checked)} />
                <label htmlFor="pg-archived" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="table-toolbar">
        <span className="pg-count">
          {ready ? `${groups.length} group${groups.length === 1 ? '' : 's'} · ${groups.reduce((n, g) => n + g.member_count, 0)} items` : ''}
        </span>
        {totalPages > 1 && (
          <PaginationControls
            pagination={{ page: current, limit: PAGE_SIZE, total: groups.length, totalPages }}
            onPageChange={(p) => setPage(p)}
          />
        )}
      </div>

      <div className="table-container">
        {!ready ? (
          <div className="pg-not-ready">{data.reason || NOT_READY_REASON}</div>
        ) : loading && !groups.length ? <LoadingSpinner /> : error ? (
          <div className="pg-not-ready">{error}</div>
        ) : (
          <table className="data-table" onContextMenu={ctxMenu.onTableContextMenu}>
            <thead>
              <tr>
                <th>Group</th>
                <th>Category</th>
                <th>Pack</th>
                <th className="text-right">Items</th>
                <th>Suppliers</th>
                <th>Additive versions</th>
                <th>Changed</th>
                <th className="actions-col" />
              </tr>
            </thead>
            <tbody>
              {!shown.length ? (
                <tr><td colSpan="8" className="text-center">
                  {filtersApplied ? 'No groups match these filters' : 'No product groups yet — they are made from the reviewed proposal, or with Add Group'}
                </td></tr>
              ) : shown.map(g => (
                <tr key={g.id} className={`pg-row${g.is_archived ? ' pg-archived' : ''}`}
                  onClick={() => setOpenId(g.id)} onContextMenu={ctxMenu.onRowContextMenu(g)}>
                  <td>
                    <div className="pg-name">{g.name}{g.is_archived && <span className="archived-badge">Archived</span>}</div>
                    <div className="pg-sub" title={g.members.map(m => `${m.item_code} ${m.item_name}`).join('\n')}>
                      {g.members.slice(0, 4).map(m => m.item_code).join(', ')}{g.members.length > 4 ? ` +${g.members.length - 4}` : ''}
                    </div>
                  </td>
                  <td>{g.category || '—'}</td>
                  <td>
                    {(g.packs || []).map(packLabel).join(', ') || '—'}
                    {(g.packs || []).length > 1 && (
                      <span className="material-symbols-rounded pg-warn-icon" title="More than one pack in one group — costs are compared pack by pack">warning</span>
                    )}
                  </td>
                  <td className="text-right">{g.member_count}</td>
                  <td>{g.suppliers.length ? g.suppliers.join(', ') : <span className="pg-muted">None in 2 years</span>}</td>
                  <td>{g.additives.length ? g.additives.join(', ') : <span className="pg-muted">—</span>}</td>
                  <td className="pg-muted">{formatDate(g.updated_at)}{g.updated_by_name ? ` · ${g.updated_by_name}` : ''}</td>
                  <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                    <RowActionsMenu actions={rowActions(g)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(openId || creating) && (
        <ProductGroupModal key={openId || 'new'} groupId={creating ? null : openId} canEdit={canEdit} onClose={closeWindow} />
      )}
      {history && (
        <AuditHistoryModal isOpen onClose={() => setHistory(null)}
          entityType="product_group" entityId={history.id} entityIdentifier={history.name} />
      )}
      <TableContextMenu
        ctx={ctxMenu}
        rowActions={rowActions}
        actions={[
          { key: 'add', icon: 'add', label: 'Add group', onClick: () => setCreating(true), disabled: !canEdit },
          { key: 'refresh', icon: 'refresh', label: 'Refresh', onClick: () => load(filters) },
          { key: 'clear-filters', icon: 'filter_alt_off', label: 'Clear filters', onClick: clearFilters }
        ]}
      />
    </div>
  );
}

export default ProductGroups;
